import React from 'react';
import { Modal, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import theme from '@/config/theme';

interface ChecklistAssignmentOnboardingProps {
  visible: boolean;
  onClose: () => void;
  onContinue: () => void;
  onWhatsApp: () => void;
}

export const ChecklistAssignmentOnboarding: React.FC<ChecklistAssignmentOnboardingProps> = ({
  visible,
  onClose,
  onContinue,
  onWhatsApp,
}) => (
  <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
    <View style={styles.overlay}>
      <LinearGradient colors={theme.colors.primary.gradient} style={styles.card}>
        <View style={styles.headerRow}>
          <Text style={styles.pill}>Primeira atribuição</Text>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Fechar dica de atribuição"
          >
            <Ionicons name="close" size={18} color={theme.colors.primary.contrast} />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Distribua o checklist para um motorista</Text>
        <Text style={styles.description}>
          O administrador pode escolher um modelo, atribuir o checklist a um motorista e já adiantar parte do preenchimento. Depois da sincronização, ele recebe o checklist no app e pode continuar o trabalho.
        </Text>

        <View style={styles.notice}>
          <Ionicons name="people-outline" size={22} color={theme.colors.primary.default} />
          <Text style={styles.noticeText}>
            Para distribuir checklists, primeiro cadastre os motoristas da empresa.
          </Text>
        </View>

        <TouchableOpacity
          style={styles.whatsAppButton}
          onPress={onWhatsApp}
          accessibilityRole="button"
          accessibilityLabel="Pedir ajuda pelo WhatsApp"
        >
          <MaterialCommunityIcons name="whatsapp" size={21} color="#25D366" />
          <Text style={styles.whatsAppText}>Ficou com dúvida? Fale com a gente</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.continueButton}
          onPress={onContinue}
          accessibilityRole="button"
          accessibilityLabel="Entendi e continuar para atribuição"
        >
          <Text style={styles.continueText}>Entendi, continuar</Text>
        </TouchableOpacity>
      </LinearGradient>
    </View>
  </Modal>
);

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.lg,
    backgroundColor: 'rgba(9, 17, 34, 0.58)',
  },
  card: {
    width: '100%',
    maxWidth: 420,
    padding: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    ...theme.shadows.md,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  pill: {
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: 3,
    borderRadius: theme.borderRadius.pill,
    color: theme.colors.primary.contrast,
    backgroundColor: 'rgba(255,255,255,0.18)',
    fontSize: theme.fontSizes.xs,
    fontWeight: theme.fontWeights.medium as any,
  },
  closeButton: {
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 17,
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
  title: {
    marginTop: theme.spacing.md,
    color: theme.colors.primary.contrast,
    fontSize: theme.fontSizes.xl,
    lineHeight: 26,
    fontWeight: theme.fontWeights.semibold as any,
  },
  description: {
    marginTop: theme.spacing.sm,
    color: 'rgba(255,255,255,0.9)',
    fontSize: theme.fontSizes.sm,
    lineHeight: 20,
  },
  notice: {
    marginTop: theme.spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    padding: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    backgroundColor: 'rgba(255,255,255,0.9)',
  },
  noticeText: {
    flex: 1,
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.sm,
    lineHeight: 19,
  },
  whatsAppButton: {
    marginTop: theme.spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    alignSelf: 'flex-start',
  },
  whatsAppText: {
    color: theme.colors.primary.contrast,
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.medium as any,
  },
  continueButton: {
    marginTop: theme.spacing.lg,
    alignItems: 'center',
    paddingVertical: 11,
    borderRadius: theme.borderRadius.pill,
    backgroundColor: theme.colors.primary.contrast,
  },
  continueText: {
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.sm,
    fontWeight: '700',
  },
});

export default ChecklistAssignmentOnboarding;
